import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import XLSXStyle from 'xlsx-js-style';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import PaymentsIcon from '@mui/icons-material/Payments';
import './SaleRegister.css';

function SalePaymentWise() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [paymentType, setPaymentType] = useState('0');
    const [searchOpen, setSearchOpen] = useState(false);

    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const d = new Date(); d.setDate(1);
        setFromDate(d.toISOString().split('T')[0]);
        setToDate(new Date().toISOString().split('T')[0]);
        fetchTableData();
    }, [isAuthenticated, companyId, token]);

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (!hasReadReportPermission) return;
        try {
            const response = await fetchData('ReportApi', '/SalePaymentWise/' + companyId + '/' + fromDate + '/' + toDate + '/' + paymentType, token);
            if (response.responseCode === '1111') setData(response.response_data);
        } catch (error) { console.error('Error fetching Sale Payment:', error); }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const filteredData = data.filter((item) =>
        Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const totalAmount = filteredData.reduce((s, r) => s + parseFloat(r.amount), 0).toFixed(2);

    const handlePrint = () => {
        const title = 'Sale Details (Payment Type Wise)';
        const rows = filteredData.map((r, i) =>
            '<tr><td>' + (i+1) + '</td><td>' + r.billno + '</td><td>' + formatDate(r.billdate) + '</td><td>' + r.paymenttype + '</td><td>' + r.tablename + '</td><td>' + r.amount + '</td></tr>'
        ).join('');
        const headers = '<th>Sl.No</th><th>Bill No</th><th>Bill Date</th><th>Payment Type</th><th>Table Name</th><th>Amount</th>';
        const pw = window.open('', '_blank');
        pw.document.write('<html><head><title>' + title + '</title></head><body>'
            + '<h3 style="font-family:Times New Roman;font-size:28px;">' + title + '</h3>'
            + '<h4 style="font-family:Times New Roman;font-size:18px;">From: ' + formatDate(fromDate) + ' To: ' + formatDate(toDate) + '</h4>'
            + '<table style="border-collapse:collapse;width:100%;"><thead style="background:#f3f3f3;"><tr style="font-size:15px;font-weight:bold;">' + headers + '</tr></thead>'
            + '<tbody style="font-size:13px;">' + rows + '</tbody></table>'
            + '<p style="text-align:right;font-weight:bold;">Total Amount: ' + totalAmount + '</p></body></html>');
        pw.document.close(); pw.print();
    };

    const handleExportExcel = () => {
        const headers = ['Sl.No', 'Bill No', 'Bill Date', 'Payment Type', 'Table Name', 'Amount'];
        const colCount = headers.length;
        const titleStyle = { font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '6D28D9' } }, alignment: { horizontal: 'center', vertical: 'center' } };
        const subStyle = { font: { bold: true, sz: 11, color: { rgb: '4C1D95' } }, fill: { fgColor: { rgb: 'EDE9FE' } }, alignment: { horizontal: 'center', vertical: 'center' } };
        const hStyle = { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '7C3AED' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: { top: { style: 'thin', color: { rgb: '5B21B6' } }, bottom: { style: 'medium', color: { rgb: '5B21B6' } }, left: { style: 'thin', color: { rgb: '5B21B6' } }, right: { style: 'thin', color: { rgb: '5B21B6' } } } };
        const cOdd = { font: { sz: 10, color: { rgb: '1F2937' } }, fill: { fgColor: { rgb: 'FFFFFF' } }, alignment: { vertical: 'center' }, border: { top: { style: 'thin', color: { rgb: 'E5E7EB' } }, bottom: { style: 'thin', color: { rgb: 'E5E7EB' } }, left: { style: 'thin', color: { rgb: 'E5E7EB' } }, right: { style: 'thin', color: { rgb: 'E5E7EB' } } } };
        const cEven = { ...cOdd, fill: { fgColor: { rgb: 'F5F3FF' } } };
        const tStyle = { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '10B981' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: { top: { style: 'medium', color: { rgb: '059669' } }, bottom: { style: 'medium', color: { rgb: '059669' } }, left: { style: 'thin', color: { rgb: '059669' } }, right: { style: 'thin', color: { rgb: '059669' } } } };
        const tNum = { ...tStyle, alignment: { horizontal: 'right', vertical: 'center' } };
        const nStyle = (even) => ({ ...(even ? cEven : cOdd), alignment: { horizontal: 'right', vertical: 'center' } });

        const ws = {}; const range = { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
        const sc = (r, c, v, s) => { const a = XLSXStyle.utils.encode_cell({ r, c }); ws[a] = { v, t: typeof v === 'number' ? 'n' : 's', s }; if (r > range.e.r) range.e.r = r; if (c > range.e.c) range.e.c = c; };

        for (let c = 0; c < colCount; c++) sc(0, c, c === 0 ? 'Sale Details (Payment Type Wise)' : '', titleStyle);
        for (let c = 0; c < colCount; c++) sc(1, c, c === 0 ? 'From : ' + formatDate(fromDate) + '   To : ' + formatDate(toDate) : '', subStyle);
        headers.forEach((h, c) => sc(2, c, h, hStyle));
        filteredData.forEach((row, i) => {
            const r = i + 3; const even = i % 2 === 1;
            sc(r, 0, i+1, even ? cEven : cOdd); sc(r, 1, row.billno, even ? cEven : cOdd); sc(r, 2, formatDate(row.billdate), even ? cEven : cOdd);
            sc(r, 3, row.paymenttype, even ? cEven : cOdd); sc(r, 4, row.tablename, even ? cEven : cOdd); sc(r, 5, parseFloat(row.amount), nStyle(even));
        });
        const tr = filteredData.length + 3;
        for (let c = 0; c < colCount - 1; c++) sc(tr, c, c === colCount - 2 ? 'TOTAL' : '', tStyle);
        sc(tr, colCount - 1, parseFloat(totalAmount), tNum);

        ws['!ref'] = XLSXStyle.utils.encode_range(range);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }];
        ws['!cols'] = [{ wch: 7 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }];
        ws['!rows'] = [{ hpt: 28 }, { hpt: 20 }, { hpt: 20 }];
        const wb = XLSXStyle.utils.book_new();
        XLSXStyle.utils.book_append_sheet(wb, ws, 'Sale Payment');
        XLSXStyle.writeFile(wb, 'SalePaymentWise.xlsx');
    };

    return (
        <div className="sale-register-container">
            <div className="sr-header-card">
                <div className="sr-header-content">
                    <div className="sr-row1">
                        <div className="sr-title-section">
                            <span className="sr-title-icon"><PaymentsIcon fontSize="small" /></span>
                            <h1 className="sr-title">Sale Payment</h1>
                        </div>
                        <select className="sr-payment-select" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                            <option value="0">All</option>
                            <option value="1">Cash</option>
                            <option value="2">Card</option>
                            <option value="3">UPI</option>
                        </select>
                    </div>
                    <div className="sr-divider" />
                    <div className="sr-controls">
                        <div className="sr-date-filters">
                            <div className="sr-date-group">
                                <label className="sr-date-label">From</label>
                                <input className="sr-date-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            </div>
                            <div className="sr-date-group">
                                <label className="sr-date-label">To</label>
                                <input className="sr-date-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                            </div>
                        </div>
                        <div className={'sr-search-wrapper' + (searchOpen ? ' open' : '')}>
                            <span className="sr-search-icon" onClick={() => setSearchOpen(function(o) { return !o; })}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input autoFocus className="sr-search-input" type="text" placeholder="Search..."
                                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }} />
                            )}
                        </div>
                        <button className="sr-action-btn sr-btn-refresh" onClick={fetchTableData}><RefreshIcon fontSize="small" />&nbsp;Refresh</button>
                        <button className="sr-action-btn sr-btn-excel" onClick={handleExportExcel}><FileDownloadIcon fontSize="small" />&nbsp;Excel</button>
                        <button className="sr-action-btn sr-btn-print" onClick={handlePrint}><PrintIcon fontSize="small" />&nbsp;Print</button>
                    </div>
                </div>
            </div>

            <div className="sr-table-wrapper">
                <TableContainer component={Paper} className="sr-table-container" elevation={0}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="sr-th">Sl.No</TableCell>
                                <TableCell className="sr-th">Bill No</TableCell>
                                <TableCell className="sr-th">Bill Date</TableCell>
                                <TableCell className="sr-th">Payment Type</TableCell>
                                <TableCell className="sr-th">Table Name</TableCell>
                                <TableCell className="sr-th" align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((row, index) => (
                                <TableRow key={index} className="sr-row">
                                    <TableCell className="sr-td">{index + 1}</TableCell>
                                    <TableCell className="sr-td">{row.billno}</TableCell>
                                    <TableCell className="sr-td">{formatDate(row.billdate)}</TableCell>
                                    <TableCell className="sr-td">{row.paymenttype}</TableCell>
                                    <TableCell className="sr-td">{row.tablename}</TableCell>
                                    <TableCell className="sr-td" align="right">{row.amount}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow className="sr-total-row">
                                <TableCell className="sr-td" colSpan={5}>Total Amount</TableCell>
                                <TableCell className="sr-td" align="right">{totalAmount}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default SalePaymentWise;
